import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bell, Edit3, Lock, Clock, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';

const PreferencesStep = ({ onNext, onSave }) => {
  const { completeOnboardingStep } = useAuth();
  const { completeStep, updateUserData, userData } = useOnboarding();
  
  const [preferences, setPreferences] = useState({
    emailNotifications: userData.userPreferences?.emailNotifications ?? true,
    signatureStyle: userData.userPreferences?.signatureStyle || 'draw',
    documentSharing: userData.userPreferences?.documentSharing || 'private',
    timeZone: userData.userPreferences?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const signatureOptions = [
    { value: 'draw', label: 'Draw signature', icon: '✏️', description: 'Draw your signature with mouse or touch' },
    { value: 'type', label: 'Type signature', icon: '⌨️', description: 'Type your name in a signature font' },
    { value: 'upload', label: 'Upload image', icon: '📸', description: 'Upload a photo of your signature' }
  ];

  const sharingOptions = [
    { value: 'private', label: 'Private (Recommended)', icon: '🔒', description: 'Documents are private by default - perfect for secure business documents' }
  ];

  const timeZones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
    { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
    { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Memoized save function to prevent stale closures
  const handleSave = useCallback(async () => {
    const updatedUserData = {
      userPreferences: preferences
    };
    
    setIsLoading(true);
    setApiError('');
    
    try {
      // Save to backend first
      await completeOnboardingStep('preferences', updatedUserData);
      // Only update local state and proceed if backend save succeeds
      updateUserData(updatedUserData);
      completeStep('preferences', updatedUserData);
      return true; // Success
    } catch (error) {
      console.error('Error saving preferences:', error);
      setApiError('Failed to save preferences. Please check your connection and try again.');
      return false; // Failed
    } finally {
      setIsLoading(false);
    }
  }, [preferences, completeOnboardingStep, updateUserData, completeStep]);

  // Expose save function to parent via onSave prop
  React.useEffect(() => {
    if (onSave) {
      onSave(handleSave);
    }
  }, [onSave, handleSave]);

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Customize your preferences
        </h2>
        <p className="text-lg text-gray-600">
          Set up your default preferences to streamline your workflow. You can always change these later.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-8"
      >
        {/* Email Notifications */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6 text-blue-500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Email Notifications</h3>
                <p className="text-sm text-gray-600">Receive updates about your documents and signatures</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.emailNotifications}
                onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Default Signature Style */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <Edit3 className="w-6 h-6 text-green-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Default Signature Style</h3>
              <p className="text-sm text-gray-600">Choose your preferred way to sign documents</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {signatureOptions.map((option) => (
              <label
                key={option.value}
                className={`flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  preferences.signatureStyle === option.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="signatureStyle"
                  value={option.value}
                  checked={preferences.signatureStyle === option.value}
                  onChange={(e) => handlePreferenceChange('signatureStyle', e.target.value)}
                  className="sr-only"
                />
                <span className="text-2xl mb-2">{option.icon}</span>
                <div className="text-center">
                  <div className="font-medium text-gray-900 mb-1">{option.label}</div>
                  <div className="text-xs text-gray-600">{option.description}</div>
                </div>
                {preferences.signatureStyle === option.value && (
                  <Check className="w-5 h-5 text-green-500 mt-2" />
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Document Security */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <Lock className="w-6 h-6 text-purple-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Document Security</h3>
              <p className="text-sm text-gray-600">Your documents are always secure</p>
            </div>
          </div>
          
          <div className="flex items-center p-4 border border-purple-200 bg-purple-50 rounded-lg">
            <span className="text-xl mr-3">🔒</span>
            <div className="flex-1">
              <div className="font-medium text-gray-900">Private by Default</div>
              <div className="text-sm text-gray-600">All documents are private by default - perfect for secure business documents. You can always share specific documents with selected recipients when needed.</div>
            </div>
            <Check className="w-5 h-5 text-purple-500" />
          </div>
        </div>

        {/* Time Zone */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-6 h-6 text-orange-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Time Zone</h3>
              <p className="text-sm text-gray-600">Used for timestamps and scheduling</p>
            </div>
          </div>
          
          <select
            value={preferences.timeZone}
            onChange={(e) => handlePreferenceChange('timeZone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            {timeZones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </motion.div>

      {apiError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{apiError}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PreferencesStep;